import { useMemo } from "react"
import { AxiosInstance } from "axios"
import { UseQueryResult } from "@tanstack/react-query"

import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { ResourceType } from "../../../../admin/organization/resources/resourcesTypes"
import { ServiceCenterStatisticsQuery } from "../../statisticsApi"

import { DASHBOARD_RESOURCE_TYPES, DashboardResourceEntry, ResourceEntry } from "./resourceDashboardTypes"

type ResourceTypeFanoutParams = Readonly<{
    serviceCenterId: number
    startDate: string
    endDate: string
    resourceType: ResourceType
}>

type FanoutFetch<TResponse> = (
    params: ResourceTypeFanoutParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
) => Promise<TResponse>

type UseResourceTypeFanoutParams<TResponse, TEntry extends ResourceEntry> = Readonly<{
    fetch: FanoutFetch<TResponse>
    queryName: ServiceCenterStatisticsQuery
    select: (data: TResponse) => TEntry[]
    serviceCenterId: number
    startDate: string
    endDate: string
    enabled?: boolean
}>

export type ResourceTypeFanoutData<TEntry extends ResourceEntry> = Readonly<{
    resultsByType: Readonly<Record<ResourceType, UseQueryResult<TEntry[]>>>
    combinedEntries: ReadonlyArray<DashboardResourceEntry<TEntry>>
    presentTypes: ReadonlyArray<ResourceType>
    isInitialLoading: boolean
    isError: boolean
}>

/**
 * Fans a per-resource dashboard query out across the resource types (the endpoint is scoped to a
 * single type). Each per-type result feeds its own table; the concatenation (`combinedEntries`,
 * tagged with `resourceType`) feeds the summary chart. `presentTypes` are the types that returned rows.
 */
export function useResourceTypeFanout<TResponse, TEntry extends ResourceEntry>({
    fetch,
    queryName,
    select,
    serviceCenterId,
    startDate,
    endDate,
    enabled = true,
}: UseResourceTypeFanoutParams<TResponse, TEntry>): ResourceTypeFanoutData<TEntry> {
    const roomResult = useApiQuery(fetch, {
        queryName,
        dependencies: { serviceCenterId, resourceType: ResourceType.ROOM, startDate, endDate },
        select,
        enabled,
    })
    const mobileAvResult = useApiQuery(fetch, {
        queryName,
        dependencies: { serviceCenterId, resourceType: ResourceType.MOBILE_AV, startDate, endDate },
        select,
        enabled,
    })
    const carResult = useApiQuery(fetch, {
        queryName,
        dependencies: { serviceCenterId, resourceType: ResourceType.CAR, startDate, endDate },
        select,
        enabled,
    })

    const resultsByType = useMemo<Readonly<Record<ResourceType, UseQueryResult<TEntry[]>>>>(
        () => ({
            [ResourceType.ROOM]: roomResult,
            [ResourceType.MOBILE_AV]: mobileAvResult,
            [ResourceType.CAR]: carResult,
        }),
        [roomResult, mobileAvResult, carResult],
    )

    const combinedEntries = useMemo<ReadonlyArray<DashboardResourceEntry<TEntry>>>(
        () =>
            DASHBOARD_RESOURCE_TYPES.flatMap((resourceType) =>
                (resultsByType[resourceType].data ?? []).map((entry) => ({ ...entry, resourceType })),
            ),
        [resultsByType],
    )

    const presentTypes = useMemo<ReadonlyArray<ResourceType>>(
        () => DASHBOARD_RESOURCE_TYPES.filter((resourceType) => (resultsByType[resourceType].data?.length ?? 0) > 0),
        [resultsByType],
    )

    const isInitialLoading =
        roomResult.isInitialLoading || mobileAvResult.isInitialLoading || carResult.isInitialLoading
    const isError = roomResult.isError && mobileAvResult.isError && carResult.isError

    return { resultsByType, combinedEntries, presentTypes, isInitialLoading, isError }
}
